import { createCustomEvent } from './custom-ng-elements/utils';
import { lazyWebComponent } from './lazy-web-component';
function hasChildrenChange(tracked, mutations) {
    for (let i = 0; i < mutations.length; i++) {
        const m = mutations[i];
        if (m.type !== 'attributes' || m.target !== tracked)
            return true;
    }
    return false;
}
function getAttributes(node) {
    const res = {};
    for (let i = 0, l = node.attributes.length; i < l; i++) {
        const attr = node.attributes[i];
        res[attr.nodeName] = attr.nodeValue;
    }
    return res;
}
function toVNode(h, node) {
    if (node.nodeType === 3) {
        return node.data.trim() ? node.data : null;
    }
    else if (node.nodeType === 1) {
        const data = {
            attrs: getAttributes(node),
            domProps: {
                innerHTML: node.innerHTML,
            },
        };
        if (data.attrs.slot) {
            data.slot = data.attrs.slot;
            delete data.attrs.slot;
        }
        return h(node.tagName, data);
    }
    else {
        return null;
    }
}
function toVNodes(h, children) {
    const res = [];
    for (let i = 0, l = children.length; i < l; i++) {
        res.push(toVNode(h, children[i]));
    }
    return res;
}
function injectHook(options, key, hook) {
    options[key] = [].concat(options[key] || []);
    options[key].unshift(hook);
}
function callHooks(vm, hook) {
    if (vm) {
        const hooks = vm.$options[hook] || [];
        hooks.forEach((h) => {
            h.call(vm);
        });
    }
}
export function lazyVueComponent(vueComponentLoader, schema) {
    async function vueComponentFactory() {
        const { default: Vue } = await AppRegistry.import(['vue2']).then(([m]) => m);
        await AppRegistry.require('@uxanalytics/core').AppContextFactory.wait();
        const { default: Component } = await vueComponentLoader();
        const options = typeof Component === 'function' ? Component.options : Component;
        const propsList = Array.isArray(options.props) ? options.props : Object.keys(options.props || {});
        // proxy $emit to native DOM events
        injectHook(options, 'beforeCreate', function () {
            const emit = this.$emit;
            this.$emit = (name, ...args) => {
                const customElement = this.$root.$options.customElement;
                const event = createCustomEvent(customElement.ownerDocument, name, args);
                customElement.dispatchEvent(event);
                return emit.call(this, name, ...args);
            };
        });
        return function componentMaker(customElement) {
            const vueComponent = new Vue({
                name: 'shadow-root',
                customElement,
                shadowRoot: customElement.shadowRoot,
                data() {
                    return {
                        props: {},
                        slotChildren: [],
                    };
                },
                render(h) {
                    return h(Component, {
                        ref: 'inner',
                        props: this.props,
                    }, this.slotChildren);
                },
            });
            /* Vue is not handling the slotted component properly */
            const observer = new MutationObserver(mutations => {
                if (hasChildrenChange(customElement, mutations)) {
                    updateChildren();
                }
            });
            observer.observe(customElement, {
                childList: true,
                subtree: true,
                characterData: true,
                attributes: true,
            });
            function updateChildren() {
                vueComponent.slotChildren = Object.freeze(toVNodes(vueComponent.$createElement, customElement.childNodes));
            }
            function mount() {
                updateChildren();
                vueComponent.$mount();
                customElement.shadowRoot.appendChild(vueComponent.$el);
            }
            vueComponent.props = propsList.reduce((props, key) => ({ ...props, [key]: undefined }), {});
            customElement.vueComponent = vueComponent;
            return {
                mount: () => {
                    vueComponent._isMounted ? callHooks(vueComponent, 'activated') : mount();
                },
                unmount: () => {
                    callHooks(vueComponent, 'deactivated');
                },
                adopted: () => { },
                call: (propName, args) => vueComponent.$refs.inner[propName](...args),
                get: (propName) => vueComponent.props[propName],
                set: (props) => {
                    Object.assign(vueComponent.props, props);
                },
            };
        };
    }
    return lazyWebComponent(vueComponentFactory, schema);
}
